/* Shared visit attribution runtime. Embed copies are generated by scripts/sync-visit-attribution.cjs. */
(function installVisitAttribution(root) {
  'use strict';
  if (!root || root.LVMVisitAttribution) return;
  const KEY = 'lvm_visit_attribution_v1';
  const TTL = 30 * 86400000;
  const MAX_BYTES = 65536;
  const DIMENSIONS = ['source', 'medium', 'campaign', 'content', 'term', 'id'];
  function enrichTouch(touch) {
    const enriched = { ...touch, utm: touch.utm.map(({ name, value }) => ({ name, value, value_lower: value.toLowerCase() })) };
    if (!enriched.utm.length && enriched.referrer) {
      enriched.source = enriched.referrer;
      enriched.medium = 'referral';
    }
    enriched.grouping = Object.fromEntries(DIMENSIONS.map(name => [name,
      typeof enriched[name] === 'string' ? enriched[name].toLowerCase() : '']));
    return enriched;
  }
  let memory = null;
  let capturedUrl = '';
  let pendingHandoff = null;
  let handoffRejected = false;
  const bytes = value => encodeURIComponent(JSON.stringify(value)).replace(/%[A-F0-9]{2}/g, '_').length;
  const meaningful = touch => Boolean(touch && (touch.utm.length || touch.fbclid || touch.ttclid || touch.gclid || touch.referrer));
  function merge(a, b) {
    const touches = [a?.first_touch, a?.last_touch, b?.first_touch, b?.last_touch].filter(Boolean);
    const signals = touches.filter(meaningful);
    const candidates = (signals.length ? signals : touches).sort((x, y) => Date.parse(x.timestamp) - Date.parse(y.timestamp));
    return candidates.length ? { first_touch: candidates[0], last_touch: candidates[candidates.length - 1] } : null;
  }
  function validTouch(touch, now) {
    return touch && typeof touch === 'object' && typeof touch.timestamp === 'string'
      && Number.isFinite(Date.parse(touch.timestamp)) && Date.parse(touch.timestamp) <= now + 300000
      && now - Date.parse(touch.timestamp) < TTL && Array.isArray(touch.utm)
      && bytes(touch) <= 32768
      && touch.utm.every(entry => entry && typeof entry.name === 'string'
        && entry.name.startsWith('utm_') && typeof entry.value === 'string');
  }
  function valid(value) {
    const now = Date.now();
    if (!value || bytes(value) > MAX_BYTES) return null;
    const first = validTouch(value.first_touch, now) ? value.first_touch : null;
    const last = validTouch(value.last_touch, now) ? value.last_touch : null;
    return first || last ? { first_touch: enrichTouch(first || last), last_touch: enrichTouch(last || first) } : null;
  }
  function parse(value) { try { return valid(JSON.parse(value)); } catch (_) { return null; } }
  function cookieRead() {
    try {
      const cookies = new Map(root.document.cookie.split(';').map(part => {
        const pos = part.indexOf('='); return [part.slice(0, pos).trim(), part.slice(pos + 1)];
      }));
      const count = Number(cookies.get(KEY));
      if (!Number.isInteger(count) || count < 1 || count > 64) return null;
      let raw = '';
      for (let i = 0; i < count; i++) {
        if (!cookies.has(KEY + '_' + i)) return null;
        raw += cookies.get(KEY + '_' + i);
      }
      return parse(decodeURIComponent(raw));
    } catch (_) { return null; }
  }
  function read() {
    let saved = null;
    try { saved = parse(root.localStorage.getItem(KEY)); } catch (_) { /* cookie fallback */ }
    return [saved, cookieRead(), valid(memory)].filter(Boolean).reduce(merge, null);
  }
  function save(value) {
    memory = value;
    const raw = JSON.stringify(value);
    try {
      root.localStorage.setItem(KEY, raw);
      if (root.localStorage.getItem(KEY) === raw) return;
    } catch (_) { /* browsers with disabled localStorage still support first-party cookies */ }
    try {
      const encoded = encodeURIComponent(raw);
      const chunks = encoded.match(/.{1,3000}/g) || [];
      // Keep the complete Cookie request header below normal proxy limits.
      // If cookies cannot hold a tag, this visit still uses the in-memory copy.
      if (chunks.length > 1) return;
      const age = Math.max(0, Math.floor((Date.parse(value.last_touch.timestamp) + TTL - Date.now()) / 1000));
      const suffix = '; Path=/; SameSite=Lax; Max-Age=' + age + (root.location.protocol === 'https:' ? '; Secure' : '');
      chunks.forEach((chunk, i) => { root.document.cookie = KEY + '_' + i + '=' + chunk + suffix; });
      root.document.cookie = KEY + '=' + chunks.length + suffix;
    } catch (_) { /* Attribution must never prevent buying. */ }
  }
  function touchFromPage(includeReferrer) {
    const url = new URL(root.location.href);
    const utm = Array.from(url.searchParams.entries()).filter(([name]) => name.startsWith('utm_'))
      .map(([name, value]) => ({ name, value }));
    let referrer = '';
    try { const ref = new URL(root.document.referrer); if (includeReferrer && ref.hostname !== url.hostname) referrer = ref.hostname; } catch (_) { /* direct */ }
    const touch = { utm, timestamp: new Date().toISOString(), referrer };
    ['source', 'medium', 'campaign', 'content', 'term', 'id'].forEach(name => {
      touch[name] = url.searchParams.get('utm_' + name) || '';
    });
    ['fbclid', 'ttclid', 'gclid'].forEach(name => { touch[name] = url.searchParams.get(name) || ''; });
    return enrichTouch(touch);
  }
  function capture() {
    try {
      let saved = merge(read(), valid(pendingHandoff));
      const href = root.location.href;
      if (capturedUrl === href && saved) return saved;
      const initialDocument = !capturedUrl;
      capturedUrl = href;
      const url = new URL(href);
      // The widget explicitly forwards the host's two touches. Do not replace
      // them with the iframe's own URL/referrer on the checkout domain.
      let fragment = '';
      if (url.hash.startsWith('#lvm_attribution=')) {
        try { fragment = decodeURIComponent(url.hash.slice('#lvm_attribution='.length)); } catch (_) { /* malformed */ }
      }
      const incoming = parse(url.searchParams.get('lvm_attribution')) || parse(fragment);
      let handoffContext = false;
      try {
        handoffContext = !handoffRejected && url.searchParams.get('embed') === '1'
          && (!root.document.referrer || new URL(root.document.referrer).origin === url.searchParams.get('lvm_parent_origin'))
          && /^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(url.searchParams.get('lvm_embed_authorization') || '');
      } catch (_) { /* An ordinary/shared URL cannot replace retained history. */ }
      if (incoming && handoffContext) {
        pendingHandoff = incoming;
        saved = merge(saved, incoming);
      } else {
        const touch = touchFromPage(initialDocument);
        if (validTouch(touch, Date.now())) saved = merge(saved, { first_touch: touch, last_touch: touch });
      }
      // Persist cross-origin history only after the cart API verifies the existing
      // signed embed capability. Until then it is an ephemeral request candidate.
      if (valid(saved) && !pendingHandoff) save(saved);
      return saved || {};
    } catch (_) { return read() || {}; }
  }
  root.LVMVisitAttribution = {
    capture,
    confirmHandoff(accepted) {
      if (!pendingHandoff) return;
      if (accepted === true) save(merge(read(), pendingHandoff));
      else handoffRejected = true;
      pendingHandoff = null;
    },
    get: () => JSON.parse(JSON.stringify(capture())),
    pageUrl() {
      try {
        const url = new URL(root.location.href);
        url.searchParams.delete('lvm_attribution');
        url.hash = '';
        return url.toString();
      } catch (_) { return ''; }
    },
    append(url) {
      try {
        const dest = new URL(url);
        const touches = capture();
        if (touches.first_touch) {
          dest.searchParams.set('lvm_attribution', JSON.stringify(touches));
          touches.last_touch.utm.forEach(({ name, value }) => { if (!dest.searchParams.has(name)) dest.searchParams.append(name, value); });
          ['fbclid', 'ttclid', 'gclid'].forEach(name => {
            if (touches.last_touch[name] && !dest.searchParams.has(name)) dest.searchParams.set(name, touches.last_touch[name]);
          });
        }
        // Fragments travel in the link and stay available to the checkout JS,
        // but are not sent as an oversized HTTP request target to the proxy.
        if ((dest.origin + dest.pathname + dest.search).length > 3500) {
          const fragmentLink = new URL(url);
          fragmentLink.hash = 'lvm_attribution=' + encodeURIComponent(JSON.stringify(touches));
          return fragmentLink.toString();
        }
        return dest.toString();
      } catch (_) { return url; }
    }
  };
  capture();
})(typeof window === 'undefined' ? null : window);
